import React, { useEffect, useState } from 'react'
import { ArrowLeft, Trophy, Heart, Delete, Play, Zap } from 'lucide-react'
import {
  ECO_MODES,
  startLength,
  makeSequence,
  wordChoices,
  checkAnswer,
} from './engine.js'
import { rngFrom } from '../lib/random.js'
import { awardBp, update } from '../lib/store.js'

const rng = () => rngFrom(Date.now() + Math.floor(Math.random() * 99999))

export function EcoGame({ onBack, onComplete }) {
  const [mode, setMode] = useState(null)
  const [phase, setPhase] = useState('show') // show | input | end
  const [level, setLevel] = useState(3)
  const [lives, setLives] = useState(2)
  const [sequence, setSequence] = useState([])
  const [choices, setChoices] = useState([])
  const [step, setStep] = useState(0)
  const [answer, setAnswer] = useState([])
  const [best, setBest] = useState(0)
  const [feedback, setFeedback] = useState(null)
  const [played, setPlayed] = useState(0)

  const beginLevel = (len, m = mode) => {
    const r = rng()
    const seq = makeSequence(r, m, len)
    setSequence(seq)
    setChoices(m === 'words' ? wordChoices(r, seq) : [])
    setStep(0)
    setAnswer([])
    setFeedback(null)
    setPhase('show')
    setLevel(len)
  }

  const startMode = (m) => {
    setMode(m)
    setLives(2)
    setBest(0)
    setPlayed(0)
    beginLevel(startLength(m), m)
  }

  // reveal sequence one item at a time
  useEffect(() => {
    if (phase !== 'show' || !mode) return
    const speed = mode === 'words' ? 950 : 780
    if (step > sequence.length) {
      const t = setTimeout(() => {
        setPhase('input')
        setStep(0)
      }, 300)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => setStep((s) => s + 1), speed)
    return () => clearTimeout(t)
  }, [phase, step, sequence, mode])

  const submit = (finalAnswer) => {
    const ok = checkAnswer(mode, sequence, finalAnswer)
    setFeedback(ok ? 'ok' : 'bad')
    if (ok) {
      const solved = sequence.length
      setBest((b) => Math.max(b, solved))
      setPlayed((p) => p + 1)
      awardBp(solved * 6)
      setTimeout(() => beginLevel(level + 1), 650)
    } else {
      const remaining = lives - 1
      setLives(remaining)
      if (remaining <= 0) {
        setTimeout(() => {
          setPhase('end')
          update((s) => {
            s.eco.plays += 1
            s.eco.best[mode] = Math.max(s.eco.best[mode] || 0, Math.max(best, sequence.length))
            return s
          })
        }, 800)
      } else {
        setTimeout(() => beginLevel(level), 800)
      }
    }
  }

  const push = (value) => {
    if (phase !== 'input' || feedback) return
    const next = [...answer, value]
    setAnswer(next)
    if (next.length >= sequence.length) setTimeout(() => submit(next), 180)
  }

  const undo = () => setAnswer((a) => a.slice(0, -1))

  // ---- MENU ----
  if (!mode) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
          >
            <ArrowLeft size={16} /> Volver
          </button>
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Eco</span>
        </div>

        <div className="p-4 rounded-2xl bg-gradient-to-br from-[#101828] to-[#0b1220] border border-slate-800">
          <div className="text-2xl">🌊</div>
          <h2 className="text-lg font-black text-white mt-1">Memoria de trabajo</h2>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            La secuencia crece hasta que fallas. Dos errores y se termina. Mide tu alcance
            (span) y compáralo contigo mismo, no con nadie más.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {ECO_MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => startMode(m.id)}
              className="text-left p-4 rounded-2xl bg-[#0e131f] border border-slate-800 hover:border-emerald-500/50 transition-all cursor-pointer"
            >
              <div className="text-xl">{m.emoji}</div>
              <div className="text-sm font-bold text-white mt-1">{m.label}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{m.hint}</div>
            </button>
          ))}
        </div>
      </div>
    )
  }

  const isVisual = mode === 'visual'
  const isWords = mode === 'words'

  // ---- END ----
  if (phase === 'end') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-5">
        <Trophy size={40} className="text-emerald-400" />
        <div>
          <h2 className="text-xl font-black text-white">Alcance: {best}</h2>
          <p className="text-xs text-slate-400 mt-1">
            {playingLabel(mode)} · resolviste {played} {played === 1 ? 'ronda' : 'rondas'}
          </p>
        </div>
        <div className="flex gap-2 w-full max-w-xs">
          <button
            type="button"
            onClick={() => startMode(mode)}
            className="flex-1 py-3 rounded-xl bg-emerald-500 text-slate-950 font-black text-sm cursor-pointer"
          >
            Otra vez
          </button>
          <button
            type="button"
            onClick={() => setMode(null)}
            className="flex-1 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white font-black text-sm cursor-pointer"
          >
            Modos
          </button>
        </div>
      </div>
    )
  }

  // ---- PLAY ----
  const showing = phase === 'show'
  const current = step >= 1 && step <= sequence.length ? sequence[step - 1] : null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setMode(null)}
          className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
        >
          <ArrowLeft size={16} /> Salir
        </button>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 rounded-lg bg-slate-900 border border-slate-800 text-[11px] font-bold text-emerald-300">
            Nivel {level}
          </span>
          <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-950/60 border border-rose-500/30 text-rose-300 text-[11px] font-bold">
            {Array.from({ length: Math.max(lives, 0) }).map((_, i) => (
              <Heart key={i} size={12} className="fill-rose-400 text-rose-400" />
            ))}
            {lives <= 0 && '—'}
          </span>
        </div>
      </div>

      {feedback && (
        <div
          className={`text-center text-sm font-black py-2 rounded-xl ${
            feedback === 'ok'
              ? 'bg-emerald-950/60 text-emerald-300 border border-emerald-500/40'
              : 'bg-rose-950/60 text-rose-300 border border-rose-500/40'
          }`}
        >
          {feedback === 'ok' ? '¡Correcto!' : 'Fallaste'}
        </div>
      )}

      {showing ? (
        <div className="flex flex-col items-center justify-center py-8 min-h-[38vh]">
          {isVisual ? (
            <div className="grid grid-cols-3 gap-2.5">
              {Array.from({ length: 9 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border transition-all duration-150 ${
                    current === i
                      ? 'bg-emerald-400 border-emerald-300 shadow-lg shadow-emerald-500/40 scale-105'
                      : 'bg-[#0e131f] border-slate-800'
                  }`}
                />
              ))}
            </div>
          ) : (
            <div className="text-6xl sm:text-7xl font-black text-white tracking-wider tabular-nums min-h-[1.2em]">
              {current != null ? current : ''}
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-6">
            {isWords ? 'Memoriza el orden' : isVisual ? 'Observa el patrón' : 'Memoriza la secuencia'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-center text-[11px] text-slate-400">
            {mode === 'reverse' ? 'Repítelo AL REVÉS' : 'Repítelo en orden'}
          </div>

          {isVisual ? (
            <div className="grid grid-cols-3 gap-2.5 justify-center mx-auto w-fit">
              {Array.from({ length: 9 }).map((_, i) => {
                const order = answer.indexOf(i)
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => (answer.includes(i) ? null : push(i))}
                    className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border transition-all cursor-pointer flex items-center justify-center ${
                      order >= 0
                        ? 'bg-emerald-500/30 border-emerald-400/60 text-emerald-200 text-xl font-black'
                        : 'bg-[#0e131f] border-slate-800 hover:border-emerald-500/40'
                    }`}
                  >
                    {order >= 0 ? order + 1 : ''}
                  </button>
                )
              })}
            </div>
          ) : isWords ? (
            <div className="grid grid-cols-2 gap-2.5">
              {choices.map((w) => {
                const used = answer.includes(w)
                return (
                  <button
                    key={w}
                    type="button"
                    disabled={used}
                    onClick={() => push(w)}
                    className={`py-3.5 rounded-xl border text-sm font-bold cursor-pointer disabled:opacity-30 ${
                      used
                        ? 'bg-emerald-950/50 border-emerald-500/40 text-emerald-300'
                        : 'bg-[#0e131f] border-slate-800 text-white hover:border-emerald-500/40'
                    }`}
                  >
                    {w}
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="max-w-xs mx-auto space-y-3">
              <div className="flex items-center justify-center gap-1.5 min-h-[2.5rem]">
                {answer.length === 0 ? (
                  <span className="text-slate-600 text-sm">…</span>
                ) : (
                  answer.map((d, i) => (
                    <span
                      key={i}
                      className="w-8 h-10 rounded-lg bg-emerald-950/50 border border-emerald-500/40 flex items-center justify-center font-black text-emerald-200"
                    >
                      {d}
                    </span>
                  ))
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => push(d)}
                    className="py-3 rounded-xl bg-[#131a29] border border-slate-700 text-lg font-black text-white hover:border-emerald-500 active:scale-95 cursor-pointer"
                  >
                    {d}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={undo}
                  className="py-3 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 flex items-center justify-center cursor-pointer"
                >
                  <Delete size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => push(0)}
                  className="py-3 rounded-xl bg-[#131a29] border border-slate-700 text-lg font-black text-white hover:border-emerald-500 active:scale-95 cursor-pointer"
                >
                  0
                </button>
                <button
                  type="button"
                  disabled={answer.length === 0}
                  onClick={() => submit(answer)}
                  className="py-3 rounded-xl bg-emerald-500 text-slate-950 font-black text-xs disabled:opacity-40 cursor-pointer flex items-center justify-center gap-1"
                >
                  <Zap size={14} /> OK
                </button>
              </div>
            </div>
          )}

          <div className="text-center text-[10px] text-slate-500">
            {answer.length}/{sequence.length}
          </div>
        </div>
      )}
    </div>
  )
}

function playingLabel(mode) {
  return (
    { digits: 'Dígitos', reverse: 'Dígitos inverso', visual: 'Patrón visual', words: 'Palabras' }[mode] ||
    mode
  )
}
